import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { InfoOutlined, ReceiptOutlined, RecordVoiceOver } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import { inject } from 'mobx-react'
import { IStores } from 'src/Stores'
import { IUser } from 'src/Definitions'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'
import moment from 'moment'
import ReactPaginate from 'react-paginate'
import DatePickerField from 'src/common/DatePickerField/DatePickerField'
import {
  getEpisodeOfCareById,
  getEpisodesOfCareByPatientId,
  searchPatients,
} from 'src/services/PatientService'
import { getPatientClaimEOCSummary } from 'src/services/LookupService'
import { ReferralContactModal } from 'src/patients/EpisodesOfCare/ReferralContactModal'
import ActionLogModal from 'src/workflow/modals/shared/ActionLogModal'
import { InboxItemType, InboxTypeItemPathConstants } from 'src/utils/InboxItemType'
import { BillStatusNumber } from 'src/utils/BillStatusNumber'
import { GetSupplementalDocumentationLookups } from 'src/services/LookupService'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { EpisodeOfCareSummaryResult } from 'src/viewModels/EpisodeOfCareSummaryResult'
import { PatientSearchRequest } from 'src/viewModels/PatientSearchRequest'
import { PatientSummaryResult } from 'src/viewModels/PatientSummaryResult'
import { SupplementalDocumentationDTO } from 'src/viewModels/workflow/SupplementalDocumentationDTO'
import { SupplementalDocumentLookupSummary } from 'src/viewModels/workflow/SupplementalDocumentLookupSummary'
import { DocumentType } from 'src/models/enums/DocumentType.enum'
import { previewSupplementalDocumentationPDF } from 'src/services/SupplementalDocumentationService'
import { ObjectiveProcessAction } from 'src/viewModels/workflow/ObjectiveProcessAction'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import { processAction, processDynamicAction } from 'src/services/WorkflowService'
import { DocumentStatus } from 'src/utils/DocumentStatus'

interface IDenialReasonSelection {
  checked: boolean
  trailingText: string
}

interface ISupplementalDocumentationModalProps {
  isOpen: boolean
  closeModal: () => void
  action: InboxActionSummary
  inboxItemId: number
  associatedItemId: string
  inboxId: string
  isDynamicItem: boolean
  inboxType: InboxItemType
  nextInboxId?: number
  rerouteToPath?: (path: string) => void
  handleProcessActionFinish?: (selectedAction?: InboxActionSummary) => void
  onSelectEpisodeOfCare?: (
    patientId: string,
    episodeOfCareId: string,
    details: SupplementalDocumentationDTO
  ) => void
  onSkip?: (details?: SupplementalDocumentationDTO) => void
  user?: IUser
}

const SupplementalDocumentationModal: React.FC<ISupplementalDocumentationModalProps> = (
  props: ISupplementalDocumentationModalProps
) => {
  console.log('SupplementalDocumentationModal props:', props)
  const isClaimWorkflow = props.inboxType === InboxItemType.Claim
  const isDocumentWorkflow = props.inboxType === InboxItemType.Document
  const documentType =
    (isDocumentWorkflow && props.action.statusId === DocumentStatus.Denied) ||
    (isClaimWorkflow && props.action.statusId === BillStatusNumber.Denied)
      ? DocumentType.ClaimDenialLetter
      : undefined
  const formikRef = React.useRef<any>(null)
  const searchValidationSchema = Yup.object().shape({
    patientDob: Yup.mixed().test('valid-date', 'Invalid date format', function (value) {
      if (!value) return true
      let dateString = value
      if (value instanceof Date) {
        dateString = moment(value).format('MM/DD/YYYY')
      }
      const isValid = moment(dateString, 'MM/DD/YYYY', true).isValid()
      return isValid
    }),
  })

  const detailsFormikRef = React.useRef<any>(null)
  const detailsValidationSchema = Yup.object().shape({
    patientFirstName: Yup.string().required('Patient First Name is required'),
    patientLastName: Yup.string().required('Patient Last Name is required'),
    patientDOB: Yup.mixed()
      .required('Patient DOB is required')
      .test('valid-date', 'Invalid date format', function (value) {
        if (!value) return true
        let dateString = value
        if (value instanceof Date) {
          dateString = moment(value).format('MM/DD/YYYY')
        }
        return moment(dateString, 'MM/DD/YYYY', true).isValid()
      }),
    dateOfService: Yup.mixed().required('Date of Service is required'),
    objectiveContactInformation: Yup.string().required(
      'Objective Contact Information is required'
    ),
    freeformAddress: Yup.string().required('Freeform Address is required'),
  })
  const [modalPage, setModalPage] = useState(isClaimWorkflow ? 0 : 1)
  const [isLoading, setIsLoading] = useState(false)
  const [claimContextError, setClaimContextError] = useState(false)
  const [gridData, setGridData] = useState<EpisodeOfCareSummaryResult[]>()
  const [selectedPatientId, setSelectedPatientId] = useState<string>()
  const [selectedPatient, setSelectedPatient] = useState<PatientSummaryResult>()
  const [selectedEpisodeOfCare, setSelectedEpisodeOfCare] =
    useState<EpisodeOfCareSummaryResult>()
  const [selectedReferralId, setSelectedReferralId] = useState<string>()
  const [isReferralContactOpen, setIsReferralContactOpen] = useState(false)
  const [isActionLogOpen, setIsActionLogOpen] = useState(false)
  const [patientGridData, setPatientGridData] = useState<PatientSummaryResult[]>()
  const [searchPatientLastName, setSearchPatientLastName] = useState('')
  const [searchPatientFirstName, setSearchPatientFirstName] = useState('')
  const [searchDob, setSearchDob] = useState('')
  const [patientPage, setPatientPage] = useState(1)
  const [patientPageCount, setPatientPageCount] = useState(0)
  const [patientTotalItems, setPatientTotalItems] = useState(0)
  const [patientFirstRecordIndex, setPatientFirstRecordIndex] = useState(0)
  const [patientLastRecordIndex, setPatientLastRecordIndex] = useState(0)
  const [denialReasonOptions, setDenialReasonOptions] =
    useState<SupplementalDocumentLookupSummary[]>()
  const [denialReasonSelections, setDenialReasonSelections] = useState<
    Record<string, IDenialReasonSelection>
  >({})
  const [denialReasonErrors, setDenialReasonErrors] = useState<Record<string, string>>({})
  const [denialReasonsGeneralError, setDenialReasonsGeneralError] = useState<string>()
  const [pdfPreviewData, setPdfPreviewData] = useState<string>()
  const [comment, setComment] = useState('')
  const [commentError, setCommentError] = useState(false)
  const [processError, setProcessError] = useState<string>()
  const [ocrProviderBillingAddress, setOcrProviderBillingAddress] = useState<string>()
  const [ocrAddressAnchor, setOcrAddressAnchor] = useState<HTMLElement | null>(null)
  const [persistedDetailsValues, setPersistedDetailsValues] = useState<
    Record<string, any>
  >({})

  const patientPageSize = 5

  useEffect(() => {
    if (documentType === undefined) {
      setDenialReasonOptions(undefined)
      return
    }

    let cancelled = false
    GetSupplementalDocumentationLookups(documentType.toString()).then((lookups) => {
      if (!cancelled) setDenialReasonOptions(lookups)
    })
    return () => {
      cancelled = true
    }
  }, [documentType])

  useEffect(() => {
    if (!props.isOpen || !isClaimWorkflow) return
    const cms1500Id = props.associatedItemId
    if (!cms1500Id) {
      setClaimContextError(true)
      return
    }

    let cancelled = false
    setIsLoading(true)
    setClaimContextError(false)
    setSelectedReferralId(undefined)
    setOcrProviderBillingAddress(undefined)
    setOcrAddressAnchor(null)
    getPatientClaimEOCSummary(cms1500Id)
      .then((summary) => {
        if (!summary) throw new Error('Claim patient and EOC summary not found')
        if (cancelled) return

        const patientDOB = moment(summary.patientDOB).toDate()
        const dateOfService = summary.dateOfService
          ? moment(summary.dateOfService).toDate()
          : ''
        const takeoverOfCareDate = summary.takeoverOfCareDate
          ? moment(summary.takeoverOfCareDate).toDate()
          : ''
        const mmiDate = summary.mmiDate ? moment(summary.mmiDate).toDate() : ''
        const episodeOfCareSummary = new EpisodeOfCareSummaryResult()
        episodeOfCareSummary.episodeOfCareId = summary.episodeOfCareId
        episodeOfCareSummary.bodyPart = summary.bodyPart ?? ''
        episodeOfCareSummary.takeoverOfCare = summary.takeoverOfCareDate
          ? moment(summary.takeoverOfCareDate).format('MM/DD/YYYY')
          : ''
        episodeOfCareSummary.lastMMIDateDisplay = summary.mmiDate
          ? moment(summary.mmiDate).format('MM/DD/YYYY')
          : ''
        episodeOfCareSummary.generatedNumberOrLegacy = summary.eocLegacy ?? ''
        setSelectedPatientId(summary.patientId)
        setSelectedPatient({
          patientId: summary.patientId,
          firstName: summary.patientFirstName,
          lastName: summary.patientLastName,
          dateOfBirth: patientDOB.toString(),
          patientIdNumber: undefined,
          preCertNumber: undefined,
        })
        setSelectedEpisodeOfCare(episodeOfCareSummary)
        setSelectedReferralId(summary.referralId)
        setOcrProviderBillingAddress(
          summary.ocrProviderBillingAddress?.trim() || undefined
        )
        setPersistedDetailsValues({
          patientFirstName: summary.patientFirstName ?? '',
          patientLastName: summary.patientLastName ?? '',
          patientDOB,
          freeformAddress: summary.providerBillingAddress ?? '',
          dateOfService,
          bodyPart: summary.bodyPart ?? '',
          takeoverOfCareDate,
          mmiDate,
          eocLegacy: summary.eocLegacy ?? '',
        })
        setModalPage(3)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Failed to load claim patient and EOC summary:', error)
        if (!cancelled) {
          setClaimContextError(true)
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [props.isOpen, isClaimWorkflow, props.associatedItemId])

  useEffect(() => {
    if (isClaimWorkflow) return

    const eocId = selectedEpisodeOfCare?.episodeOfCareId
    setSelectedReferralId(undefined)
    setIsReferralContactOpen(false)
    if (!selectedPatientId || !eocId) return

    let cancelled = false
    getEpisodeOfCareById(eocId)
      .then((eoc) => {
        if (!cancelled) setSelectedReferralId(eoc.referralId)
      })
      .catch((error) => console.error('Failed to load referral contact:', error))

    return () => {
      cancelled = true
    }
  }, [isClaimWorkflow, selectedPatientId, selectedEpisodeOfCare?.episodeOfCareId])

  function close() {
    props.closeModal()
    setIsReferralContactOpen(false)
    setIsActionLogOpen(false)
    setModalPage(isClaimWorkflow ? 0 : 1)
    setGridData(undefined)
    setSelectedPatientId(undefined)
    setSelectedPatient(undefined)
    setSelectedEpisodeOfCare(undefined)
    setDenialReasonSelections({})
    setDenialReasonErrors({})
    setDenialReasonsGeneralError(undefined)
    setPdfPreviewData(undefined)
    setComment('')
    setCommentError(false)
    setProcessError(undefined)
    setOcrProviderBillingAddress(undefined)
    setOcrAddressAnchor(null)
    setPersistedDetailsValues({})
    clearSearch()
  }

  function toggleDenialReason(description: string, checked: boolean) {
    setDenialReasonSelections((current) => ({
      ...current,
      [description]: {
        checked,
        trailingText: checked ? (current[description]?.trailingText ?? '') : '',
      },
    }))
    if (checked) {
      setDenialReasonsGeneralError(undefined)
    } else {
      setDenialReasonErrors((current) => {
        const { [description]: _removed, ...rest } = current
        return rest
      })
    }
  }

  function setDenialReasonText(description: string, value: string) {
    setDenialReasonSelections((current) => ({
      ...current,
      [description]: {
        checked: current[description]?.checked ?? false,
        trailingText: value,
      },
    }))
  }

  function getDenialReasonKey(reason: SupplementalDocumentLookupSummary, index: number) {
    return reason.description ?? `__standalone_${index}`
  }

  // a checked reason with a leading/trailing text box requires that box to be filled in
  function validateDenialReasons(): boolean {
    const hasAnyChecked = Object.values(denialReasonSelections).some(
      (selection) => selection.checked
    )
    if (!hasAnyChecked) {
      setDenialReasonsGeneralError('At least one denial reason must be selected')
      return false
    }
    setDenialReasonsGeneralError(undefined)

    const errors: Record<string, string> = {}
    ;(denialReasonOptions ?? []).forEach((reason, index) => {
      const key = getDenialReasonKey(reason, index)
      const selection = denialReasonSelections[key]
      if (!selection?.checked) return
      if (reason.trailingText && !selection.trailingText?.trim()) {
        errors[key] = 'This field is required'
      }
    })
    setDenialReasonErrors(errors)
    return Object.keys(errors).length === 0
  }

  function handleNextClick() {
    if (!validateDenialReasons()) return
    detailsFormikRef.current?.submitForm()
  }

  function skip() {
    setSelectedPatientId(undefined)
    setSelectedPatient(undefined)
    setSelectedEpisodeOfCare(undefined)
    setPersistedDetailsValues({})
    setModalPage(3)
  }

  function skipEpisodeOfCare() {
    setSelectedEpisodeOfCare(undefined)
    clearPersistedDetailsFields([
      'bodyPart',
      'takeoverOfCareDate',
      'mmiDate',
      'eocLegacy',
    ])
    setModalPage(3)
  }

  function clearPersistedDetailsFields(fields: string[]) {
    setPersistedDetailsValues((current) => {
      const next = { ...current }
      fields.forEach((field) => delete next[field])
      return next
    })
  }

  function search(event: any) {
    const formValues = formikRef.current?.values
    if (formValues) {
      setSearchPatientFirstName(formValues.patientFirst || '')
      setSearchPatientLastName(formValues.patientLast || '')
      setSearchDob(formValues.patientDob || '')
    }
    setPatientPage(1)
    getPatientData(
      formValues?.patientFirst || '',
      formValues?.patientLast || '',
      formValues?.patientDob || '',
      1
    )
    event.preventDefault()
  }

  function clearSearch() {
    formikRef.current?.resetForm({
      values: {
        patientFirst: '',
        patientLast: '',
        patientDob: '',
      },
    })

    setPatientGridData(undefined)
    setSearchPatientFirstName('')
    setSearchPatientLastName('')
    setSearchDob('')
    setPatientPage(1)
    setPatientPageCount(0)
    setPatientTotalItems(0)
  }

  async function getPatientData(
    firstName?: string,
    lastName?: string,
    dob?: string,
    page?: number
  ) {
    setIsLoading(true)
    const requestData = new PatientSearchRequest()
    requestData.page = page ?? patientPage
    requestData.pageSize = patientPageSize
    requestData.patientFirstName = firstName ?? searchPatientFirstName
    requestData.patientLastName = lastName ?? searchPatientLastName
    const targetDob = dob ?? searchDob
    requestData.dateOfBirth = targetDob
      ? moment(targetDob).format('YYYY-MM-DD')
      : undefined
    await searchPatients(requestData).then((response: any) => {
      setPatientTotalItems(response.totalItems)
      setPatientPageCount(response.totalPages)
      setPatientGridData(response.items)
      setPaginationOffsetData(page ?? patientPage, response.totalItems)
      setIsLoading(false)
    })
  }

  function isGridDataEmpty() {
    return patientTotalItems === 0
  }

  function paginationInfo() {
    if (!isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {patientFirstRecordIndex} to {patientLastRecordIndex} of{' '}
            {patientTotalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  function mapPatientGridData() {
    if (isGridDataEmpty()) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={6} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return patientGridData?.map((patient) => (
        <TableRow key={patient.patientId} className="gridPadding">
          <TableCell>
            <Button
              color="primary"
              variant="contained"
              onClick={() => setSelectedPatientRow(patient)}
            >
              Select
            </Button>
          </TableCell>
          <TableCell style={{ paddingLeft: '30px' }}>{patient.patientIdNumber}</TableCell>
          <TableCell>{patient.preCertNumber}</TableCell>
          <TableCell>{patient.lastName}</TableCell>
          <TableCell>{patient.firstName}</TableCell>
          <TableCell align="right">
            {patient.dateOfBirth
              ? moment.utc(patient.dateOfBirth).format('MM/DD/YYYY')
              : ''}
          </TableCell>
        </TableRow>
      ))
    }
  }

  function setPaginationOffsetData(page: number, totalItems: number) {
    const firstRecordIndex = (page - 1) * patientPageSize + 1
    const lastRecordIndex =
      page * patientPageSize < totalItems ? page * patientPageSize : totalItems
    setPatientFirstRecordIndex(firstRecordIndex)
    setPatientLastRecordIndex(lastRecordIndex)
  }

  function handlePageChange(event: any) {
    const page = event.selected + 1
    setPatientPage(page)
    getPatientData(undefined, undefined, undefined, page)
  }

  const setSelectedPatientRow = (patient: PatientSummaryResult) => {
    clearPersistedDetailsFields([
      'patientFirstName',
      'patientLastName',
      'patientDOB',
      'bodyPart',
      'takeoverOfCareDate',
      'mmiDate',
      'eocLegacy',
    ])
    setSelectedPatient(patient)
    setSelectedPatientId(patient.patientId!)
    setModalPage(2)
    getEOCData(patient.patientId!)
  }

  const getEOCData = async (patientId: string) => {
    setIsLoading(true)
    const requestData = new SearchRequestBase()
    requestData.page = 1
    requestData.pageSize = 100
    await getEpisodesOfCareByPatientId(requestData, patientId).then((response: any) => {
      setIsLoading(false)
      setGridData(response.items)
      // no EOCs to pick from, so there's nothing to show on the EOC page
      if (!response.items || response.items.length === 0) {
        setModalPage(3)
      }
    })
  }

  const selectEpisodeOfCare = (eoc: EpisodeOfCareSummaryResult) => {
    if (!eoc.episodeOfCareId || !selectedPatientId) return
    clearPersistedDetailsFields([
      'bodyPart',
      'takeoverOfCareDate',
      'mmiDate',
      'eocLegacy',
    ])
    setSelectedEpisodeOfCare(eoc)
    setModalPage(3)
  }

  function buildSupplementalDocumentationDetails(values: any) {
    const details = new SupplementalDocumentationDTO()
    details.documentType = documentType
    details.patientId = selectedPatientId
    details.episodeOfCareId = selectedEpisodeOfCare?.episodeOfCareId
    details.cms1500Id = isClaimWorkflow ? props.associatedItemId : undefined
    details.patientFirstName = values?.patientFirstName
    details.patientLastName = values?.patientLastName
    details.patientDOB = values?.patientDOB
      ? moment(values.patientDOB).toDate()
      : undefined
    details.freeformAddress = values?.freeformAddress
    details.dateOfService = values?.dateOfService
      ? moment(values.dateOfService).toDate()
      : undefined
    details.eocLegacy = values?.eocLegacy
    details.bodyPart = values?.bodyPart
    details.takeoverOfCareDate = values?.takeoverOfCareDate
      ? moment(values.takeoverOfCareDate).toDate()
      : undefined
    details.mmiDate = values?.mmiDate ? moment(values.mmiDate).toDate() : undefined
    details.attentionInformation = values?.attentionInformation
    details.objectiveContactInformation = values?.objectiveContactInformation
    details.claimDenialReasons = (denialReasonOptions ?? [])
      .map((reason, index) => {
        const selection = denialReasonSelections[getDenialReasonKey(reason, index)]
        if (!selection?.checked) return undefined
        return {
          ...reason,
          trailingTextValue: selection.trailingText?.trim() ?? '',
        }
      })
      .filter((reason): reason is SupplementalDocumentLookupSummary => !!reason)
    return details
  }

  async function previewDocument(values?: any) {
    if (documentType === undefined) return

    const resolvedValues = values ?? detailsFormikRef.current?.values
    setPersistedDetailsValues(resolvedValues)
    const details = buildSupplementalDocumentationDetails(resolvedValues)
    setIsLoading(true)
    try {
      const pdf = await previewSupplementalDocumentationPDF(
        documentType.toString(),
        details
      )
      const blob = new Blob([pdf], { type: 'application/pdf' })
      setPdfPreviewData(URL.createObjectURL(blob))
      setModalPage(4)
    } finally {
      setIsLoading(false)
    }
  }

  async function handleFinish() {
    if (props.action.requireComment && !comment.trim()) {
      setCommentError(true)
      return
    }

    setCommentError(false)
    setProcessError(undefined)
    const data = new ObjectiveProcessAction()
    if (props.action.displayComment && comment.trim()) {
      data.comment = comment.trim()
    }
    data.supplementalDocumentationDTO =
      buildSupplementalDocumentationDetails(persistedDetailsValues)

    setIsLoading(true)
    try {
      const response = props.isDynamicItem
        ? await processDynamicAction(
            data,
            props.inboxId,
            props.action.id.toString(),
            props.associatedItemId
          )
        : await processAction(
            data,
            props.action.id.toString(),
            props.inboxItemId.toString()
          )

      const nextInboxId =
        response.nextAssociatedItem?.inboxId ??
        props.action.nextInboxId ??
        props.nextInboxId ??
        props.inboxId

      if (
        response.nextAssociatedItem &&
        nextInboxId != undefined &&
        props.rerouteToPath
      ) {
        const nextItemType = response.nextAssociatedItem.itemType?.toString() as
          | InboxItemType
          | undefined
        props.rerouteToPath(
          `/workflow/${InboxTypeItemPathConstants[nextItemType ?? props.inboxType]}/${response.nextAssociatedItem.itemId}/inbox/${nextInboxId}`
        )
      } else {
        props.handleProcessActionFinish?.(props.action)
        close()
      }
    } catch (error) {
      console.error('Failed to process supplemental documentation:', error)
      setProcessError(
        error instanceof Error
          ? error.message
          : 'Failed to process supplemental documentation. Please try again.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  const mapGridData = () => {
    if (gridData == undefined || gridData?.length <= 0) {
      return (
        <TableRow key={1}>
          <TableCell colSpan={9} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return gridData?.map((eoc, index) => (
        <TableRow key={index} className="gridPadding">
          <TableCell>
            <Button
              onClick={() => selectEpisodeOfCare(eoc)}
              color="primary"
              variant="contained"
            >
              Select
            </Button>
          </TableCell>
          <TableCell> {eoc.referralDateDisplay} </TableCell>
          <TableCell>{eoc.takeoverOfCare}</TableCell>
          <TableCell>{eoc.generatedNumberOrLegacy}</TableCell>
          <TableCell>{eoc.status}</TableCell>
          <TableCell>{eoc.surgeryDateDisplay} </TableCell>
          <TableCell>{eoc.lastMMIDateDisplay}</TableCell>
          <TableCell>{eoc.careCoordinatorName}</TableCell>
          <TableCell>{eoc.bodyPart}</TableCell>
        </TableRow>
      ))
    }
  }

  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth={false}
        open={props.isOpen!}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            close()
          }
        }}
      >
        <DialogTitle style={{ padding: 0 }}>
          <ResponsiveAppBar
            isModal={true}
            title={
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {props.action.actionName}
                {modalPage === 3 && (
                  <Tooltip title="View Action Log">
                    <IconButton
                      color="inherit"
                      aria-label="View Action Log"
                      onClick={() => setIsActionLogOpen(true)}
                    >
                      <ReceiptOutlined fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </span>
            }
          />
        </DialogTitle>
        <DialogContent style={{ padding: '12px 12px 8px', overflowX: 'hidden' }}>
          {isLoading && (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1200,
              }}
            >
              <CircularProgress size={100} />
            </div>
          )}
          {modalPage == 1 && !isClaimWorkflow && (
            <Formik
              initialValues={{ patientFirst: '', patientLast: '', patientDob: '' }}
              initialTouched={{
                patientFirst: false,
                patientLast: false,
                patientDob: false,
              }}
              onSubmit={search}
              validationSchema={searchValidationSchema}
              innerRef={formikRef}
              validateOnChange={true}
              validateOnBlur={true}
            >
              {(formik) => (
                <form>
                  <Grid item container direction="column" spacing={2} xs={12}>
                    <Grid item>
                      <Typography
                        color="primary"
                        component="h6"
                        variant="h6"
                        gutterBottom
                        style={{ paddingBottom: '10px' }}
                      >
                        SEARCH CRITERIA
                      </Typography>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={2}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Patient First Name"
                              name="patientFirst"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Patient Last Name"
                              name="patientLast"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={2}>
                          <DatePickerField
                            inputId="dob"
                            variant="outlined"
                            name="patientDob"
                            label="Date of Birth"
                          />
                        </Grid>
                        <Grid item direction="row" spacing={2}>
                          <Button
                            type="submit"
                            color="primary"
                            size="medium"
                            variant="contained"
                            style={{ marginRight: '10px' }}
                            onClick={search}
                            disabled={!!formik.errors.patientDob}
                          >
                            Search
                          </Button>
                          <Button
                            color="secondary"
                            onClick={clearSearch}
                            size="medium"
                            variant="contained"
                          >
                            Clear Search
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12} style={{ paddingTop: '20px' }}>
                      <Table aria-label="simple table">
                        <TableHead>
                          <TableRow className="gridPadding">
                            <TableCell />
                            <TableCell style={{ paddingLeft: '30px' }}>
                              Patient Id
                            </TableCell>
                            <TableCell>Pre-Cert #</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>First Name</TableCell>
                            <TableCell align="right">DOB</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody style={{ width: '100%' }}>
                          {mapPatientGridData()}
                        </TableBody>
                      </Table>
                      <div className="pagination-row">
                        {paginationInfo()}
                        <ReactPaginate
                          forcePage={patientPage - 1}
                          previousLabel={'<'}
                          nextLabel={'>'}
                          onPageChange={handlePageChange}
                          pageCount={patientPageCount}
                          containerClassName={'pagination'}
                          activeClassName={'active'}
                          //@ts-ignore
                          renderOnZeroPageCount={null}
                        />
                      </div>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
          {modalPage == 2 && !isClaimWorkflow && (
            <Grid item xs={12}>
              <Typography variant="body1" style={{ color: 'grey' }}>
                Select an Episode of Care to attach the supplemental documentation to, or
                skip this step.
              </Typography>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow className="gridPadding">
                    <TableCell></TableCell>
                    <TableCell>Referral Date</TableCell>
                    <TableCell>Takeover of Care</TableCell>
                    <TableCell>EOC/Legacy</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Surgery Date</TableCell>
                    <TableCell>MMI</TableCell>
                    <TableCell>Care Coordinator</TableCell>
                    <TableCell>Body Part</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>{mapGridData()}</TableBody>
              </Table>
            </Grid>
          )}
          {claimContextError && (
            <Typography color="error">
              Unable to load the claim patient and episode of care. Close and try again.
            </Typography>
          )}
          {modalPage == 3 && (
            <Formik
              initialValues={{
                patientFirstName:
                  persistedDetailsValues.patientFirstName ??
                  selectedPatient?.firstName ??
                  '',
                patientLastName:
                  persistedDetailsValues.patientLastName ??
                  selectedPatient?.lastName ??
                  '',
                patientDOB:
                  persistedDetailsValues.patientDOB ??
                  (selectedPatient?.dateOfBirth
                    ? moment.utc(selectedPatient.dateOfBirth).format('MM/DD/YYYY')
                    : ''),
                bodyPart:
                  persistedDetailsValues.bodyPart ??
                  selectedEpisodeOfCare?.bodyPart ??
                  '',
                takeoverOfCareDate:
                  persistedDetailsValues.takeoverOfCareDate ??
                  selectedEpisodeOfCare?.takeoverOfCare ??
                  '',
                mmiDate:
                  persistedDetailsValues.mmiDate ??
                  (selectedEpisodeOfCare?.lastMMIDateDisplay &&
                  moment(
                    selectedEpisodeOfCare.lastMMIDateDisplay,
                    ['MM/DD/YYYY', 'YYYY-MM-DD'],
                    true
                  ).isValid()
                    ? moment(
                        selectedEpisodeOfCare.lastMMIDateDisplay,
                        ['MM/DD/YYYY', 'YYYY-MM-DD'],
                        true
                      ).toDate()
                    : undefined) ??
                  '',
                freeformAddress: persistedDetailsValues.freeformAddress ?? '',
                dateOfService: persistedDetailsValues.dateOfService ?? '',
                eocLegacy:
                  selectedEpisodeOfCare?.generatedNumberOrLegacy ??
                  persistedDetailsValues.eocLegacy ??
                  '',
                attentionInformation: persistedDetailsValues.attentionInformation ?? '',
                objectiveContactInformation:
                  persistedDetailsValues.objectiveContactInformation ??
                  [
                    [props.user?.firstName, props.user?.lastName]
                      .filter((part) => !!part)
                      .join(' '),
                    props.user?.email,
                  ]
                    .filter((line) => !!line)
                    .join('\n'),
              }}
              onSubmit={previewDocument}
              validationSchema={detailsValidationSchema}
              innerRef={detailsFormikRef}
            >
              {(formik) => (
                <form>
                  <Grid item container direction="row" spacing={2} xs={12}>
                    <Grid
                      item
                      container
                      direction="column"
                      spacing={2}
                      xs={5}
                      style={{ paddingTop: '30px' }}
                    >
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={4}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Patient First Name *"
                              name="patientFirstName"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              error={
                                formik.touched.patientFirstName &&
                                !!formik.errors.patientFirstName
                              }
                              helperText={
                                formik.touched.patientFirstName &&
                                formik.errors.patientFirstName
                              }
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                          <FormControl sx={{ flex: 1 }}>
                            <Field
                              as={TextField}
                              label="Patient Last Name *"
                              name="patientLastName"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              error={
                                formik.touched.patientLastName &&
                                !!formik.errors.patientLastName
                              }
                              helperText={
                                formik.touched.patientLastName &&
                                formik.errors.patientLastName
                              }
                            />
                          </FormControl>
                          {selectedReferralId && (
                            <Tooltip title="Show Referral Contact">
                              <RecordVoiceOver
                                fontSize="small"
                                color="primary"
                                style={{ cursor: 'pointer', marginLeft: '8px' }}
                                onClick={() => setIsReferralContactOpen(true)}
                              />
                            </Tooltip>
                          )}
                        </Grid>
                        <Grid item xs={4}>
                          <DatePickerField
                            inputId="patientDOB"
                            variant="outlined"
                            name="patientDOB"
                            label="Patient DOB *"
                            fullWidth
                            errorMessage={
                              formik.touched.patientDOB
                                ? (formik.errors.patientDOB as string)
                                : undefined
                            }
                          />
                        </Grid>
                      </Grid>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={4}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Body Part"
                              name="bodyPart"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                          <DatePickerField
                            inputId="takeoverOfCareDate"
                            variant="outlined"
                            name="takeoverOfCareDate"
                            label="Takeover of Care Date"
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <DatePickerField
                            inputId="mmiDate"
                            variant="outlined"
                            name="mmiDate"
                            label="MMI Date"
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={4}>
                          <DatePickerField
                            inputId="dateOfService"
                            variant="outlined"
                            name="dateOfService"
                            label="Date of Service"
                          />
                        </Grid>
                        {selectedEpisodeOfCare && (
                          <Grid item xs={4}>
                            <FormControl fullWidth>
                              <Field
                                as={TextField}
                                label="EOC/Legacy"
                                name="eocLegacy"
                                InputLabelProps={{ shrink: true }}
                                variant="outlined"
                                disabled={isClaimWorkflow}
                              />
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={12}>
                          {isClaimWorkflow && ocrProviderBillingAddress && (
                            <>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  paddingBottom: '4px',
                                }}
                              >
                                <Tooltip title="Click to view OCR Provider Billing Address">
                                  <IconButton
                                    size="small"
                                    aria-label="View OCR provider address"
                                    onClick={(event) =>
                                      setOcrAddressAnchor(event.currentTarget)
                                    }
                                    sx={{ color: '#000' }}
                                  >
                                    <InfoOutlined fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                              <Popover
                                open={Boolean(ocrAddressAnchor)}
                                anchorEl={ocrAddressAnchor}
                                onClose={() => setOcrAddressAnchor(null)}
                                anchorOrigin={{
                                  vertical: 'bottom',
                                  horizontal: 'right',
                                }}
                                transformOrigin={{
                                  vertical: 'top',
                                  horizontal: 'right',
                                }}
                              >
                                <Typography
                                  sx={{ p: 1.5, maxWidth: 320, whiteSpace: 'pre-line' }}
                                >
                                  {ocrProviderBillingAddress}
                                </Typography>
                              </Popover>
                            </>
                          )}
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Freeform Address *"
                              name="freeformAddress"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              multiline
                              minRows={3}
                              error={
                                formik.touched.freeformAddress &&
                                !!formik.errors.freeformAddress
                              }
                              helperText={
                                formik.touched.freeformAddress &&
                                formik.errors.freeformAddress
                              }
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Attention Information"
                              name="attentionInformation"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              multiline
                              minRows={3}
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid item container direction="row" spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <Field
                              as={TextField}
                              label="Objective Contact Information *"
                              name="objectiveContactInformation"
                              InputLabelProps={{ shrink: true }}
                              variant="outlined"
                              multiline
                              minRows={3}
                              error={
                                formik.touched.objectiveContactInformation &&
                                !!formik.errors.objectiveContactInformation
                              }
                              helperText={
                                formik.touched.objectiveContactInformation &&
                                formik.errors.objectiveContactInformation
                              }
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={7}>
                      <Typography variant="subtitle2" gutterBottom>
                        Claim Denial Reasons *
                      </Typography>
                      {denialReasonsGeneralError && (
                        <Typography
                          variant="caption"
                          color="error"
                          gutterBottom
                          component="div"
                        >
                          {denialReasonsGeneralError}
                        </Typography>
                      )}
                      {denialReasonOptions?.map((reason, index) => {
                        const key = getDenialReasonKey(reason, index)
                        const selection = denialReasonSelections[key]

                        if (!reason.description) {
                          const isMultiline = (reason.textLines ?? 1) > 1
                          return (
                            <Grid
                              item
                              container
                              direction="row"
                              alignItems={isMultiline ? 'flex-start' : 'center'}
                              spacing={0}
                              key={key}
                            >
                              <Grid item>
                                <FormControlLabel
                                  sx={{ marginRight: 0 }}
                                  control={
                                    <Checkbox
                                      checked={selection?.checked ?? false}
                                      onChange={(event) =>
                                        toggleDenialReason(key, event.target.checked)
                                      }
                                    />
                                  }
                                  label=""
                                />
                              </Grid>
                              <Grid item xs>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  multiline={isMultiline}
                                  minRows={isMultiline ? reason.textLines : undefined}
                                  disabled={!selection?.checked}
                                  error={!!denialReasonErrors[key]}
                                  helperText={denialReasonErrors[key]}
                                  value={selection?.trailingText ?? ''}
                                  onChange={(event) =>
                                    setDenialReasonText(key, event.target.value)
                                  }
                                />
                              </Grid>
                            </Grid>
                          )
                        }

                        const isMultiline = (reason.textLines ?? 1) > 1
                        return (
                          <Grid
                            item
                            container
                            direction="row"
                            alignItems={isMultiline ? 'flex-start' : 'center'}
                            spacing={1}
                            key={key}
                          >
                            <Grid item>
                              <FormControlLabel
                                sx={{ marginRight: '4px' }}
                                control={
                                  <Checkbox
                                    checked={selection?.checked ?? false}
                                    onChange={(event) =>
                                      toggleDenialReason(
                                        reason.description!,
                                        event.target.checked
                                      )
                                    }
                                  />
                                }
                                label={reason.description}
                              />
                            </Grid>
                            {reason.trailingText && (
                              <Grid item xs>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  multiline={isMultiline}
                                  minRows={isMultiline ? reason.textLines : undefined}
                                  disabled={!selection?.checked}
                                  error={!!denialReasonErrors[key]}
                                  helperText={denialReasonErrors[key]}
                                  value={selection?.trailingText ?? ''}
                                  onChange={(event) =>
                                    setDenialReasonText(
                                      reason.description!,
                                      event.target.value
                                    )
                                  }
                                />
                              </Grid>
                            )}
                          </Grid>
                        )
                      })}
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          )}
          {modalPage == 4 && pdfPreviewData && (
            <>
              {processError && (
                <Grid item xs={12}>
                  <Typography color="error" role="alert">
                    {processError}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} style={{ height: '55vh' }}>
                <iframe
                  title="Supplemental Documentation Preview"
                  src={pdfPreviewData}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Grid>
              {props.action.displayComment && (
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <TextField
                    label="Comments"
                    name="comment"
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    multiline
                    minRows={4}
                    required={props.action.requireComment}
                    value={comment}
                    onChange={(event) => {
                      setComment(event.target.value)
                      if (event.target.value.trim()) setCommentError(false)
                    }}
                    error={commentError}
                    helperText={commentError ? 'Comments are required' : undefined}
                  />
                </Grid>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {modalPage == 2 && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setModalPage(1)
                setGridData(undefined)
                setSelectedPatientId(undefined)
              }}
            >
              Back
            </Button>
          )}
          {modalPage == 3 && (
            <>
              {!isClaimWorkflow && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setPersistedDetailsValues(detailsFormikRef.current?.values ?? {})
                    setSelectedEpisodeOfCare(undefined)
                    setModalPage(selectedPatientId ? 2 : 1)
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleNextClick}
                disabled={
                  isClaimWorkflow && (!selectedPatientId || !selectedEpisodeOfCare)
                }
              >
                Next
              </Button>
            </>
          )}
          {modalPage == 4 && (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => {
                  setModalPage(3)
                  setPdfPreviewData(undefined)
                }}
              >
                Back
              </Button>
              <Button variant="contained" color="primary" onClick={handleFinish}>
                Finish
              </Button>
            </>
          )}
          {!isClaimWorkflow && (modalPage == 1 || modalPage == 2) && (
            <Button
              variant="contained"
              color="primary"
              onClick={modalPage == 2 ? skipEpisodeOfCare : skip}
            >
              Skip
            </Button>
          )}
          <Button
            onClick={() => {
              close()
            }}
            data-cy="close"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {isActionLogOpen && (
        <ActionLogModal
          isOpen={isActionLogOpen}
          closeModal={() => setIsActionLogOpen(false)}
          associatedItemId={props.associatedItemId}
          inboxType={props.inboxType}
        />
      )}
      {isReferralContactOpen && selectedReferralId && (
        <ReferralContactModal
          key={selectedReferralId}
          selectedReferralId={selectedReferralId}
          isOpen={isReferralContactOpen}
          close={() => setIsReferralContactOpen(false)}
        />
      )}
    </>
  )
}

export default inject<
  IStores,
  ISupplementalDocumentationModalProps,
  Partial<ISupplementalDocumentationModalProps>,
  any
>((stores: IStores) => ({
  user: stores.global.user,
  rerouteToPath: stores.global.rerouteToPath,
}))(SupplementalDocumentationModal)
